import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import LeadsService from '@/services/LeadsService'

import PersonalInfoSection from '../common/PersonalInfoSection'
import LeadDetailsSection from '../common/LeadDetailsSection'

import useLeadFormState from '../list/hooks/useLeadFormState'
import { submitLead, validateLead } from './submitLead'

export default function LeadAdd() {
	const navigate = useNavigate()
	const { form, setField, reset } = useLeadFormState()
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)

	// Single submit handler (guard against double-submit)
	const handleSubmit = useCallback(
		async e => {
			e.preventDefault()
			if (loading) return

			setError(null)

			const { ok, errors } = validateLead(form)
			if (!ok) {
				setError(errors.join(' '))
				return
			}

			setLoading(true)
			try {
				const { success, error } = await submitLead(LeadsService, form)
				if (success) {
					const name = form.customerName?.trim() || 'Lead'
					// Reset local form state before route change
					reset()
					navigate('/leads', {
						state: { message: `${name} has been created successfully.` },
					})
				} else {
					setError(error || 'Failed to create lead.')
				}
			} catch (err) {
				setError('An unexpected error occurred while creating the lead.')
			} finally {
				setLoading(false)
			}
		},
		[form, loading, navigate, reset]
	)

	const handleCancel = useCallback(() => navigate('/leads'), [navigate])

	return (
		<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
			<div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
				{/* Header */}
				<div className="px-6 py-8 border-b border-gray-200">
					<div className="max-w-2xl">
						<h2 className="text-2xl font-bold leading-7 text-gray-900">Lead Information</h2>
						<p className="mt-2 text-sm leading-6 text-gray-600">
							Add a new lead with their personal details and status
						</p>
					</div>
				</div>

				{/* Form */}
				<form onSubmit={handleSubmit} className="px-6 py-8">
					{error && (
						<div className="mb-6 rounded-lg bg-red-50 p-4 border border-red-200">
							<div className="text-sm text-red-700">{error}</div>
						</div>
					)}

					<div className="space-y-8">
						<PersonalInfoSection form={form} setField={setField} />
						<LeadDetailsSection form={form} setField={setField} />

						{/* Notes */}
						<div>
							<h3 className="text-lg font-semibold text-gray-900 mb-6">Additional Information</h3>
							<div>
								<label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-2">
									Notes
								</label>
								<textarea
									id="note"
									name="note"
									rows={4}
									value={form.note}
									onChange={e => setField('note', e.target.value)}
									placeholder="Add any additional notes about this lead..."
									className="block w-full rounded-lg border-0 py-2.5 px-3.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm resize-none transition-colors"
								/>
							</div>
						</div>
					</div>

					{/* Actions */}
					<div className="mt-10 pt-6 border-t border-gray-200">
						<div className="flex items-center gap-4">
							<button
								type="submit"
								disabled={loading}
								className={`inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 min-w-[120px] ${
									loading
										? 'bg-gray-400 text-white cursor-not-allowed'
										: 'bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-md'
								}`}>
								{loading ? (
									<>
										<svg
											className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24">
											<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
											<path
												className="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
											/>
										</svg>
										Creating...
									</>
								) : (
									'Create Lead'
								)}
							</button>

							<button
								type="button"
								onClick={handleCancel}
								className="inline-flex items-center justify-center px-6 py-2.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 min-w-[120px]">
								Cancel
							</button>
						</div>
					</div>
				</form>
			</div>
		</div>
	)
}
